import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { Eye, EyeOff, Lock, Mail, Menu, X } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import Logo from '../../components/Logo/Logo';
import VersionBadge from '../../components/VersionBadge';

const Login = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  // Set when we bounced the user here after their session expired (see
  // config/authExpiry). Shows a calm notice instead of a raw error.
  const [sessionExpired] = useState(() => {
    if (typeof window === 'undefined') return false;
    const expired = new URLSearchParams(window.location.search).get('expired') === '1';
    if (expired) window.history.replaceState({}, '', window.location.pathname); // clean the URL
    return expired;
  });
  const { login } = useAuth();
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  const onSubmit = async (data) => {
    console.log('🚀 Form submitted with data:', data);
    setIsLoading(true);
    try {
      console.log('🔐 Calling login function...');
      const result = await login(data.email, data.password);
      console.log('📊 Login result:', result);
      
      if (result.success) {
        console.log('✅ Login successful, navigating to dashboard...');
        navigate('/app/dashboard');
      } else {
        console.log('❌ Login failed:', result.error);
      }
    } catch (error) {
      console.error('💥 Login error caught in component:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center">
              <Link to="/" className="flex-shrink-0">
                <Logo variant="color" size="lg" />
              </Link>
            </div>
            
            {/* Desktop Navigation */}
            <nav className="hidden md:flex space-x-8">
              <Link to="/" className="text-gray-500 hover:text-gray-900">
                Home
              </Link>
              <Link to="/contact" className="text-gray-500 hover:text-gray-900">
                Contact
              </Link>
              <Link to="/about" className="text-gray-500 hover:text-gray-900">
                About
              </Link>
            </nav>

            {/* Mobile menu button */}
            <div className="md:hidden">
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="text-gray-700 hover:text-gray-900"
                title="Toggle menu"
              >
                {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </button>
            </div>
          </div>

          {/* Mobile Navigation */}
          {isMenuOpen && (
            <div className="md:hidden py-4 border-t border-gray-200">
              <nav className="space-y-4">
                <Link to="/" className="block text-gray-500 hover:text-gray-900 py-2">Home</Link>
                <Link to="/contact" className="block text-gray-500 hover:text-gray-900 py-2">Contact</Link>
                <Link to="/about" className="block text-gray-500 hover:text-gray-900 py-2">About</Link>
              </nav>
            </div>
          )}
        </div>
      </header>

      {/* Login Form */}
      <div className="relative flex items-center justify-center overflow-hidden bg-slate-950 py-16 px-4 sm:px-6 lg:px-8">
        {/* Drifting gradient blobs + faint grid (matches the landing hero) */}
        <div className="pointer-events-none absolute inset-0">
          <div className="hero-blob" style={{ width: 460, height: 460, top: -120, left: -80, background: 'radial-gradient(circle, #2563eb, transparent 70%)' }} />
          <div className="hero-blob" style={{ width: 420, height: 420, bottom: -140, right: -80, background: 'radial-gradient(circle, #7c3aed, transparent 70%)', animationDelay: '6s' }} />
          <div className="hero-grid" />
        </div>
        <div className="relative max-w-md w-full space-y-6 rounded-2xl bg-white p-8 shadow-2xl">
        <div>
          <div className="mx-auto h-12 w-12 flex items-center justify-center rounded-full bg-primary-100">
            <Lock className="h-8 w-8 text-primary-600" />
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Sign in to PharmaChargeback
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Manage your pharmaceutical contracts and chargebacks
          </p>
        </div>

        {sessionExpired && (
          <div className="mt-6 rounded-md bg-blue-50 border border-blue-200 px-4 py-3 text-center text-sm text-blue-800">
            Your session timed out. Please sign in again to continue.
          </div>
        )}

        <form className="mt-8 space-y-6" onSubmit={handleSubmit(onSubmit)}>
          <div className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email address
              </label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  className={`appearance-none relative block w-full pl-10 pr-3 py-2 border ${
                    errors.email ? 'border-error-300' : 'border-gray-300'
                  } placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm`}
                  placeholder="Enter your email"
                  {...register('email', {
                    required: 'Email is required',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Invalid email address'
                    }
                  })}
                />
              </div>
              {errors.email && (
                <p className="mt-1 text-sm text-error-600">{errors.email.message}</p>
              )}
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Password
              </label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  className={`appearance-none relative block w-full pl-10 pr-10 py-2 border ${
                    errors.password ? 'border-error-300' : 'border-gray-300'
                  } placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm`}
                  placeholder="Enter your password"
                  {...register('password', {
                    required: 'Password is required',
                    minLength: {
                      value: 6,
                      message: 'Password must be at least 6 characters'
                    }
                  })}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                  title="Toggle password visibility"
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                  ) : (
                    <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                  )}
                </button>
              </div>
              {errors.password && (
                <p className="mt-1 text-sm text-error-600">{errors.password.message}</p>
              )}
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={isLoading}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Sign in"
            >
              {isLoading ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Signing in...
                </div>
              ) : (
                'Sign in'
              )}
            </button>
          </div>

          <div className="text-center">
            <Link
              to="/forgot-password"
              className="font-medium text-primary-600 hover:text-primary-500"
            >
              Forgot your password?
            </Link>
          </div>
          
          <div className="text-center">
            <p className="text-xs text-gray-500">
              Demo Credentials:<br />
              Admin: admin@pharmachargeback.com / password123<br />
              Staff: staff@pharmachargeback.com / password123<br />
              Customer: customer@pharmachargeback.com / password123
            </p>
          </div>
          <div className="text-center pt-1">
            <VersionBadge />
          </div>
        </form>
        </div>
      </div>
    </div>
  );
};

export default Login;
